import {
  Component,
  OnInit,
  inject,
  ViewChild,
  TemplateRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../../../../core/services/settings.service';
import { appointmentStatusKey } from '../../../../core/utils/appointment-status.utils';
import { selectBranchId } from '../../../../core/store/auth/auth.selectors';
import { toast } from 'ngx-sonner';
import { firstValueFrom } from 'rxjs';
import {
  AppPrimeTable,
  PrimeTableColumn,
} from '../../../../shared/components/prime-table/prime-table';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ColorPickerModule } from 'primeng/colorpicker';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { MODULE, PermissionService } from '../../../../core/services/permission.service';

@Component({
  selector: 'app-agenda-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    AppPrimeTable,
    AppButton,
    DialogModule,
    InputTextModule,
    SelectModule,
    ColorPickerModule,
    RadioButtonModule,
    ConfirmDialogModule,
    HasPermissionDirective,
  ],
  templateUrl: './agenda-tab.html',
})
export class AgendaTab implements OnInit {
  private fb = inject(FormBuilder);
  private store = inject(Store);
  private settingsService = inject(SettingsService);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  private confirmationService = inject(ConfirmationService);
  private perm = inject(PermissionService);

  get canCreate(): boolean {
    return this.perm.canCreate(MODULE.CONFIGURACOES);
  }
  get canEdit(): boolean {
    return this.perm.canEdit(MODULE.CONFIGURACOES);
  }
  get canDelete(): boolean {
    return this.perm.canDelete(MODULE.CONFIGURACOES);
  }

  getCleanHex(value: string | null | undefined): string {
    if (!value) return '000000';
    return value.replace(/#/g, '').trim();
  }

  updateHex(controlName: string, cleanHex: string, form: FormGroup) {
    if (!cleanHex) return;
    const hex = '#' + cleanHex.replace(/#/g, '').toUpperCase();
    form.get(controlName)?.setValue(hex);
  }

  formatInputHex(controlName: string, form: FormGroup) {
    const control = form.get(controlName);
    if (!control) return;
    let val = (control.value || '').trim();
    if (!val) return;

    val = '#' + val.replace(/#/g, '').toUpperCase();
    control.setValue(val);
  }

  @ViewChild('typeColorTimeTpl', { static: true }) typeColorTimeTpl!: TemplateRef<any>;
  @ViewChild('typeBgTimeTpl', { static: true }) typeBgTimeTpl!: TemplateRef<any>;
  @ViewChild('typeActionsTpl', { static: true }) typeActionsTpl!: TemplateRef<any>;

  @ViewChild('statusNameTpl', { static: true }) statusNameTpl!: TemplateRef<any>;
  @ViewChild('statusColorTextTpl', { static: true }) statusColorTextTpl!: TemplateRef<any>;
  @ViewChild('statusBgCardTpl', { static: true }) statusBgCardTpl!: TemplateRef<any>;
  @ViewChild('statusActionsTpl', { static: true }) statusActionsTpl!: TemplateRef<any>;

  typeColors: any[] = [];
  statusColors: any[] = [];
  appointmentStatuses: any[] = [];

  loadingTypes = false;
  loadingStatuses = false;

  branchId: number | null = null;

  typeColumns: PrimeTableColumn[] = [
    { field: 'name', header: 'agenda_category' },
    { field: 'color_time', header: 'agenda_time_text_color' },
    { field: 'bg_time', header: 'agenda_time_bg_color' },
    { field: 'actions', header: 'agenda_actions', styleClass: 'w-20' },
  ];

  statusColumns: PrimeTableColumn[] = [
    { field: 'status_name', header: 'agenda_status' },
    { field: 'color_text', header: 'agenda_text_color' },
    { field: 'bg_card', header: 'agenda_card_color' },
    { field: 'actions', header: 'agenda_actions', styleClass: 'w-20' },
  ];

  get typeTemplates() {
    return {
      color_time: this.typeColorTimeTpl,
      bg_time: this.typeBgTimeTpl,
      actions: this.typeActionsTpl,
    };
  }

  get statusTemplates() {
    return {
      status_name: this.statusNameTpl,
      color_text: this.statusColorTextTpl,
      bg_card: this.statusBgCardTpl,
      actions: this.statusActionsTpl,
    };
  }

  /** Translated status label (i18n key via the shared map, else the raw name). */
  getStatusLabel(statusName: string | null | undefined): string {
    return this.translate.instant(appointmentStatusKey(statusName));
  }

  // Type Color Form
  showTypeModal = false;
  typeForm: FormGroup = this.fb.group({
    name: ['', Validators.required],
    color_time: ['#FFFFFF', Validators.required],
    bg_time: ['#000000', Validators.required],
  });

  // Status Color Form
  showStatusModal = false;
  statusForm: FormGroup = this.fb.group({
    appointment_status_id: [null, Validators.required],
    color_text: ['#FFFFFF', Validators.required],
    bg_card: ['#000000', Validators.required],
  });

  isEditStatus = false;
  editStatusId: number | null = null;
  statusNameForEdit: string = '';

  ngOnInit() {
    this.store.select(selectBranchId).subscribe((id) => {
      this.branchId = id;
      if (id) {
        this.loadData();
      }
    });

    this.settingsService.getAppointmentStatuses().subscribe((res) => {
      this.appointmentStatuses = res;
    });

    // Keep preview label in sync with selected status
    this.statusForm.get('appointment_status_id')?.valueChanges.subscribe((id) => {
      const match = this.appointmentStatuses.find((s: any) => s.id === id);
      if (match) {
        this.statusNameForEdit = match.status_name || match.name || '';
      }
    });
  }

  loadData() {
    this.loadingTypes = true;
    this.settingsService.getTypeColors().subscribe({
      next: (res) => {
        this.typeColors = res;
        this.loadingTypes = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading type colors', err);
        this.loadingTypes = false;
        this.cdr.detectChanges();
      },
    });

    this.loadingStatuses = true;
    this.settingsService.getStatusColors().subscribe({
      next: (res) => {
        this.statusColors = res.map((item) => ({
          ...item,
          status_name: item.status_name || item.name,
        }));
        this.loadingStatuses = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading status colors', err);
        this.loadingStatuses = false;
        this.cdr.detectChanges();
      },
    });
  }

  getAvailableStatuses(): any[] {
    const withLabel = (list: any[]) =>
      list.map((s) => ({ ...s, label: this.getStatusLabel(s.status_name || s.name) }));

    if (this.isEditStatus) {
      return withLabel(this.appointmentStatuses);
    }
    return withLabel(
      this.appointmentStatuses.filter((status) => {
        const isTaken = this.statusColors.some(
          (sc) =>
            sc.appointment_status_id === status.id ||
            sc.id_status === status.id ||
            sc.id === status.id ||
            (sc.status_name &&
              status.status_name &&
              sc.status_name.toLowerCase() === status.status_name.toLowerCase())
        );
        return !isTaken;
      })
    );
  }

  // Type Colors
  isEditType = false;
  editTypeId: number | null = null;

  openTypeModal(editItem?: any) {
    if (editItem ? !this.canEdit : !this.canCreate) return;
    if (editItem) {
      this.isEditType = true;
      this.editTypeId = editItem.id;
      this.typeForm.patchValue({
        name: editItem.name,
        color_time: editItem.color_time,
        bg_time: editItem.bg_time,
      });
    } else {
      this.isEditType = false;
      this.editTypeId = null;
      this.typeForm.reset({ color_time: '#FFFFFF', bg_time: '#000000' });
    }
    this.showTypeModal = true;
  }

  closeTypeModal() {
    this.showTypeModal = false;
    this.isEditType = false;
    this.editTypeId = null;
  }

  async submitType() {
    if (this.isEditType ? !this.canEdit : !this.canCreate) return;
    if (this.typeForm.invalid || !this.branchId) return;

    const formData = {
      ...this.typeForm.value,
      branches_id: this.branchId,
    };

    try {
      if (this.isEditType && this.editTypeId) {
        await firstValueFrom(this.settingsService.updateTypeColor(this.editTypeId, formData));
        toast.success(this.translate.instant('agenda_success_update_category'));
      } else {
        await firstValueFrom(this.settingsService.createTypeColor(formData));
        toast.success(this.translate.instant('agenda_success_add_category'));
      }
      this.loadData();
      this.closeTypeModal();
    } catch (err) {
      if (this.isEditType) {
        toast.error(this.translate.instant('agenda_error_update_category'));
      } else {
        toast.error(this.translate.instant('agenda_error_add_category'));
      }
      console.error(err);
    }
  }

  // Status Colors
  openStatusModal(editItem?: any) {
    if (editItem ? !this.canEdit : !this.canCreate) return;
    if (editItem) {
      console.log('DEBUG: Editing Status Color Item:', editItem);
      console.log('DEBUG: Available Options:', this.appointmentStatuses);

      this.isEditStatus = true;
      this.editStatusId = editItem.id;
      this.statusNameForEdit = editItem.status_name || editItem.name;

      // Ensure we handle different possible ID field names from Supabase RPC
      let statusId = editItem.appointment_status_id || editItem.status_id || editItem.id_status;

      // Fallback: If statusId is null (e.g. because we are configuring it for the first time or the RPC only returns color row ID),
      // look it up in appointmentStatuses by name
      if (!statusId && this.statusNameForEdit) {
        const match = this.appointmentStatuses.find(
          (s: any) =>
            (s.status_name &&
              s.status_name.toLowerCase() === this.statusNameForEdit.toLowerCase()) ||
            (s.name && s.name.toLowerCase() === this.statusNameForEdit.toLowerCase())
        );
        if (match) {
          statusId = match.id;
        }
      }

      // If not matched by name, fall back to editItem.id
      if (!statusId) {
        statusId = editItem.id;
      }

      // Convert to number if it's a numeric string to prevent type mismatch with p-select options
      if (
        statusId !== undefined &&
        statusId !== null &&
        typeof statusId === 'string' &&
        !isNaN(Number(statusId))
      ) {
        statusId = Number(statusId);
      }

      console.log('DEBUG: Extracted Status ID for Form:', statusId);

      this.statusForm.patchValue({
        appointment_status_id: statusId,
        color_text: editItem.color_text,
        bg_card: editItem.bg_card,
      });

      this.statusForm.get('appointment_status_id')?.disable();
      console.log('DEBUG: Form Value after patch:', this.statusForm.value);
    } else {
      this.isEditStatus = false;
      this.editStatusId = null;
      this.statusNameForEdit = '';
      this.statusForm.reset({
        color_text: '#FFFFFF',
        bg_card: '#000000',
        appointment_status_id: null,
      });
      this.statusForm.get('appointment_status_id')?.enable();
    }
    this.showStatusModal = true;
  }

  closeStatusModal() {
    this.showStatusModal = false;
    this.statusForm.get('appointment_status_id')?.enable();
  }

  async submitStatus() {
    if (this.isEditStatus ? !this.canEdit : !this.canCreate) return;
    if (this.statusForm.invalid || !this.branchId) return;

    const formData = {
      ...this.statusForm.getRawValue(),
      branches_id: this.branchId,
      is_global: false,
    };

    try {
      if (this.isEditStatus && this.editStatusId) {
        await firstValueFrom(this.settingsService.updateStatusColor(this.editStatusId, formData));
        toast.success(this.translate.instant('agenda_success_update_status'));
      } else {
        await firstValueFrom(this.settingsService.createStatusColor(formData));
        toast.success(this.translate.instant('agenda_success_add_status'));
      }
      this.loadData();
      this.closeStatusModal();
    } catch (err) {
      toast.error(this.translate.instant('agenda_error_save_status'));
      console.error(err);
    }
  }

  // Deletion logic
  deleteType(id: number) {
    if (!this.canDelete) return;
    this.confirmationService.confirm({
      message: this.translate.instant('agenda_confirm_delete_category'),
      header: this.translate.instant('confirm_delete'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('yes'),
      rejectLabel: this.translate.instant('common.no'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: async () => {
        try {
          await firstValueFrom(this.settingsService.deleteTypeColor(id));
          toast.success(this.translate.instant('agenda_success_delete_category'));
          this.loadData();
        } catch (err) {
          toast.error(this.translate.instant('agenda_error_delete_category'));
          console.error(err);
        }
      },
    });
  }

  deleteStatus(id: number) {
    if (!this.canDelete) return;
    this.confirmationService.confirm({
      message: this.translate.instant('agenda_confirm_delete_status'),
      header: this.translate.instant('confirm_delete'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('yes'),
      rejectLabel: this.translate.instant('common.no'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: async () => {
        try {
          await firstValueFrom(this.settingsService.deleteStatusColor(id));
          toast.success(this.translate.instant('agenda_success_delete_status'));
          this.loadData();
        } catch (err) {
          toast.error(this.translate.instant('agenda_error_delete_status'));
          console.error(err);
        }
      },
    });
  }
}
